import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  TrendingUp, TrendingDown, Building2, Euro, 
  ShoppingCart, Hammer, Store, CheckCircle2,
  Wallet, Target, BarChart3, RefreshCw,
  Receipt, Percent, Landmark,
  ClipboardCheck, Calendar, CalendarDays, Handshake, AlertTriangle, Bell, Gavel, Sunrise, ChevronRight
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

const TODO_ICONS = {
  ClipboardCheck, Calendar, CalendarDays, Handshake, AlertTriangle, Bell, Gavel, TrendingUp,
};

const StatCard = ({ icon: Icon, label, value, suffix = '', trend, color = 'emerald' }) => {
  const colors = {
    emerald: 'text-emerald-500',
    blue: 'text-blue-500',
    amber: 'text-amber-500',
    purple: 'text-purple-500',
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800 card-hover">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="stat-label mb-2">{label}</p>
            <p className="stat-value text-white">
              {typeof value === 'number' ? value.toLocaleString('fr-FR') : value}
              {suffix && <span className="text-lg ml-1">{suffix}</span>}
            </p>
          </div>
          <div className={`p-3 rounded-lg bg-zinc-800 ${colors[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
        {trend !== undefined && (
          <div className={`flex items-center mt-3 text-sm ${trend >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
            {trend >= 0 ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
            <span>{Math.abs(trend)}% vs mois dernier</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const COLORS = ['#71717A', '#F59E0B', '#3B82F6', '#A855F7', '#F97316', '#10B981'];

export const DashboardPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [todo, setTodo] = useState({ items: [], date: null });
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    try {
      const [statsRes, projectsRes, todoRes] = await Promise.all([
        axios.get(`${API}/stats`, { withCredentials: true }),
        axios.get(`${API}/projects`, { withCredentials: true }),
        axios.get(`${API}/dashboard/todo`, { withCredentials: true }).catch(() => ({ data: { items: [], date: null } })),
      ]);
      setStats(statsRes.data);
      setProjects(projectsRes.data);
      setTodo(todoRes.data || { items: [], date: null });
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pipelineData = useMemo(() => [
    { name: 'Prospection', value: stats?.nb_projets_prospection || 0, color: '#71717A' },
    { name: 'En cours achat', value: stats?.nb_projets_en_cours_achat || 0, color: '#F59E0B' },
    { name: 'Achetés', value: stats?.nb_projets_achetes || 0, color: '#3B82F6' },
    { name: 'En rénovation', value: stats?.nb_projets_en_renovation || 0, color: '#A855F7' },
    { name: 'En vente', value: stats?.nb_projets_en_vente || 0, color: '#F97316' },
    { name: 'Vendus', value: stats?.nb_projets_vendus || 0, color: '#10B981' },
  ], [stats]);

  const visiblePipelineData = useMemo(() => pipelineData.filter(d => d.value > 0), [pipelineData]);
  const totalProjects = useMemo(() => pipelineData.reduce((acc, item) => acc + item.value, 0), [pipelineData]);
  const recentProjects = useMemo(() => projects.slice(0, 5), [projects]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-10 w-48 bg-zinc-800 rounded" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-32 bg-zinc-800 rounded-lg" />)}
        </div>
      </div>
    );
  }

  const statusLabels = {
    prospection: 'Prospection',
    en_cours_achat: 'En cours d\'achat',
    achete: 'Acheté',
    en_renovation: 'En rénovation',
    en_vente: 'En vente',
    vendu: 'Vendu'
  };

  return (
    <div className="space-y-8 animate-fadeIn" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit'] tracking-tight">Tableau de bord</h1>
          <p className="text-zinc-400 mt-1">Vue d'ensemble de votre activite</p>
        </div>
        <button onClick={() => { setLoading(true); loadData(); }}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 text-sm transition-colors" data-testid="refresh-button">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Actualiser
        </button>
      </div>

      {/* "Ce qui t'attend aujourd'hui" - actionable todo block */}
      <Card className="bg-gradient-to-br from-zinc-900 via-zinc-900 to-emerald-950/20 border-zinc-800" data-testid="dashboard-todo-section">
        <CardHeader className="pb-3">
          <CardTitle className="text-white flex items-center gap-2 text-lg">
            <Sunrise className="w-5 h-5 text-amber-400" />
            Ce qui t'attend aujourd'hui
            {todo.date && (
              <span className="text-xs text-zinc-500 font-normal ml-2">
                {new Date(todo.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {todo.items && todo.items.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="dashboard-todo-list">
              {todo.items.map((item) => {
                const Icon = TODO_ICONS[item.icon] || ClipboardCheck;
                return (
                  <button
                    key={item.id}
                    onClick={() => navigate(item.link)}
                    className="group flex items-start gap-3 p-3 rounded-lg border border-zinc-800 bg-zinc-950/40 hover:border-zinc-600 hover:bg-zinc-900 transition-all text-left"
                    data-testid={`dashboard-todo-${item.id}`}
                  >
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${item.color}20`, color: item.color }}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-bold font-mono" style={{ color: item.color }}>{item.count}</span>
                        <span className="text-sm text-zinc-200 font-medium truncate">{item.label}</span>
                      </div>
                      <p className="text-[11px] text-zinc-500 truncate mt-0.5">{item.detail}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-zinc-300 flex-shrink-0 mt-3" />
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center gap-3 text-zinc-500 text-sm py-4">
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              Rien d'urgent aujourd'hui. Profites-en pour avancer sur tes projets en prospection.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div title={`Capital initial: ${(stats?.capital_initial || 0).toLocaleString('fr-FR')} €\n− Apport immobilisé (projets en cours): ${(stats?.cash_immobilise || 0).toLocaleString('fr-FR')} €\n+ Plus-value nette encaissée (projets vendus): ${(stats?.cash_in_pv_nette || 0).toLocaleString('fr-FR')} €\n\nLogique MdB en prêt in fine : seul votre apport sort à l'achat, la banque finance le reste. À la vente, vous récupérez l'apport + la plus-value nette (frais annexes, IS et TVA déduits).`}>
          <StatCard
            icon={Wallet}
            label="Trésorerie société"
            value={stats?.solde_compte || 0}
            suffix="€"
            color="emerald"
          />
          <p className="text-[10px] text-zinc-600 mt-2 px-1 leading-tight">
            Capital {(stats?.capital_initial || 0).toLocaleString('fr-FR')} − Apport immo. {(stats?.cash_immobilise || 0).toLocaleString('fr-FR')} + PV nette {(stats?.cash_in_pv_nette || 0).toLocaleString('fr-FR')}
          </p>
        </div>
        <StatCard
          icon={Euro}
          label="Total investi"
          value={stats?.total_investi || 0}
          suffix="€"
          color="blue"
        />
        <StatCard
          icon={TrendingUp}
          label="Plus-value brute"
          value={stats?.total_plus_value_brute || 0}
          suffix="€"
          color="emerald"
        />
        <StatCard
          icon={Target}
          label="Plus-value nette"
          value={stats?.total_plus_value_nette || 0}
          suffix="€"
          color="purple"
        />
      </div>

      {/* Breakdown of fees/taxes on sold projects */}
      {(stats?.nb_projets_vendus || 0) > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="dashboard-tax-breakdown">
          <StatCard
            icon={Receipt}
            label="Frais totaux (notaire, portage, agence…)"
            value={stats?.total_frais || 0}
            suffix="€"
            color="amber"
          />
          <StatCard
            icon={Percent}
            label="TVA sur marge payée"
            value={stats?.total_tva || 0}
            suffix="€"
            color="amber"
          />
          <StatCard
            icon={Landmark}
            label="Impôt sur les sociétés (IS)"
            value={stats?.total_is || 0}
            suffix="€"
            color="amber"
          />
        </div>
      )}

      {/* Pipeline Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {pipelineData.map((item) => (
          <Card key={item.name} className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold font-mono" style={{ color: item.color }}>
                {item.value}
              </p>
              <p className="text-xs text-zinc-500 mt-1">{item.name}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pipeline Bar Chart */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-emerald-500" />
              Pipeline des projets
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={pipelineData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" horizontal={false} />
                  <XAxis type="number" stroke="#71717A" fontSize={12} />
                  <YAxis dataKey="name" type="category" stroke="#71717A" fontSize={11} width={90} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#18181B', border: '1px solid #27272A', borderRadius: '8px' }}
                    labelStyle={{ color: '#FAFAFA' }}
                  />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {pipelineData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Pie Chart */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-emerald-500" />
              Répartition des projets
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center justify-center">
              {totalProjects > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={visiblePipelineData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {visiblePipelineData.map((entry) => (
                        <Cell key={`cell-${entry.name}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#18181B', border: '1px solid #27272A', borderRadius: '8px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-zinc-500">Aucun projet pour le moment</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Projects */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-lg text-white">Projets récents</CardTitle>
        </CardHeader>
        <CardContent>
          {recentProjects.length > 0 ? (
            <div className="space-y-3">
              {recentProjects.map((project) => (
                <div 
                  key={project.id} 
                  onClick={() => navigate(`/app/pipeline?project_id=${project.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/app/pipeline?project_id=${project.id}`); }}
                  className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg hover:bg-zinc-800 hover:border-emerald-500/40 border border-transparent transition-colors cursor-pointer"
                  data-testid={`dashboard-recent-project-${project.id}`}
                  title="Ouvrir dans le Pipeline"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-zinc-700 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-zinc-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">{project.property_title || 'Sans titre'}</p>
                      <p className="text-xs text-zinc-500">{project.property_city || 'Ville non définie'}</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium badge-${project.status}`}>
                    {statusLabels[project.status] || project.status}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-zinc-500 py-8">
              Aucun projet pour le moment. Commencez par ajouter un bien !
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
